import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface DocumentAccessRequest {
  id: string;
  conversation_id: string;
  listing_id: string;
  requester_id: string;
  requested_documents: string[];
  request_message?: string;
  status: 'pending' | 'approved' | 'denied';
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
}

export const useDocumentAccessRequests = (conversationId?: string) => {
  const [requests, setRequests] = useState<DocumentAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchRequests = async () => {
    if (!conversationId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('document_access_requests' as any)
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests((data as any) || []);
    } catch (error) {
      console.error('Error fetching document access requests:', error);
      toast({
        title: 'Error loading document requests',
        description: 'Failed to load document access requests',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const createRequest = async (
    listingId: string,
    documentIds: string[],
    message?: string
  ): Promise<boolean> => {
    if (!conversationId) return false;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: newRequest, error } = await supabase
        .from('document_access_requests' as any)
        .insert({
          conversation_id: conversationId,
          listing_id: listingId,
          requester_id: user.id,
          requested_documents: documentIds,
          request_message: message,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      // Insert system message with request_id in metadata for reliable matching
      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: user.id,
        message_type: 'document_request',
        content: message || 'Requested access to documents',
        metadata: { request_id: (newRequest as any).id }
      });

      toast({
        title: 'Request sent',
        description: 'Your document access request has been sent to the seller'
      });

      await fetchRequests();
      return true;
    } catch (error) {
      console.error('Error creating document request:', error);
      toast({
        title: 'Request failed',
        description: 'Failed to send document access request',
        variant: 'destructive'
      });
      return false;
    }
  };

  const approveRequest = async (requestId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the request details
      const { data: request, error: fetchError } = await supabase
        .from('document_access_requests' as any)
        .select('*')
        .eq('id', requestId)
        .single();

      if (fetchError) throw fetchError;

      // Create grants for all requested documents
      const grants = (request as any).requested_documents.map((docId: string) => ({
        document_id: docId,
        listing_id: (request as any).listing_id,
        granted_to_user_id: (request as any).requester_id,
        granted_by_user_id: user.id,
        access_request_id: requestId
      }));

      const { error: grantsError } = await supabase
        .from('document_access_grants' as any)
        .insert(grants);

      if (grantsError) throw grantsError;

      // Update request status
      const { error: updateError } = await supabase
        .from('document_access_requests' as any)
        .update({
          status: 'approved',
          approved_by: user.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (updateError) throw updateError;

      // Insert system message
      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: user.id,
        message_type: 'document_granted',
        content: `Access granted to ${(request as any).requested_documents.length} document(s)`
      });

      toast({
        title: 'Access granted',
        description: 'Document access has been granted successfully'
      });

      await fetchRequests();
      return true;
    } catch (error) {
      console.error('Error approving request:', error);
      toast({
        title: 'Approval failed',
        description: 'Failed to grant document access',
        variant: 'destructive'
      });
      return false;
    }
  };

  const denyRequest = async (requestId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('document_access_requests' as any)
        .update({
          status: 'denied',
          approved_by: user.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      // Insert system message
      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: user.id,
        message_type: 'system',
        content: 'Document access request was denied'
      });

      toast({
        title: 'Request denied',
        description: 'Document access request has been denied'
      });

      await fetchRequests();
      return true;
    } catch (error) {
      console.error('Error denying request:', error);
      toast({
        title: 'Action failed',
        description: 'Failed to deny document access request',
        variant: 'destructive'
      });
      return false;
    }
  };

  useEffect(() => {
    fetchRequests();

    if (!conversationId) return;

    const channel = supabase
      .channel(`doc-requests-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_access_requests',
          filter: `conversation_id=eq.${conversationId}`
        },
        () => {
          fetchRequests();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId]);

  return {
    requests,
    loading,
    createRequest,
    approveRequest,
    denyRequest,
    refetch: fetchRequests
  };
};
